import {
  AggregationGroupBy,
  AllCommentsGenericQuery,
  LogsTemplate,
  LogTemplateType,
  ShiftType,
  WbsFilterOption,
  WorkpackTypeFilterOption,
} from '@/__generated__/graphql';
import { SnackbarMessage } from '@/types';
import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import { KpiWorkLogFormContextValue, QaqcKpiTemplate } from './types';

export type QaqcPanelOpenIntent = 'fill' | 'view';

export const kpiWorkLogFormContextAtom = atom<KpiWorkLogFormContextValue | null>(null);

export const kpiPanelOpen = atom(false);

export const kpiPlatformWorkpackDrawerOpen = atom(false);

/** Controls the Indirect POB dialog's open state. Mirrors `kpiPanelOpen` so the dialog can be
 * rendered at the drawer level instead of inside every button that opens it. */
export const indirectPobDialogOpen = atom(false);

/** Structure id for the Indirect POB dialog flow. Set by the button that opens the dialog so the
 * dialog doesn't need to rely on URL-level structure context. */
export const indirectPobDialogStructureId = atom<string | undefined>(undefined);

/** When set, overrides `useGetStructureId` inside the KPI panel flow. Used when opening the panel
 * from a page that has no URL-level structure context (e.g. the platform page's KPI table). */
export const kpiPanelStructureIdOverride = atom<string | undefined>(undefined);

/** Snackbar messages produced by the KPI panel flow. Rendered by `KpiPanelDrawer` so messages
 * surface on whichever page hosts the drawer. */
export const kpiPanelSnackbarMessage = atom<SnackbarMessage>({ shouldShow: false });

/** Snapshot of the page-level WBS filter captured before the cost-log panel opens. `KpiCostLogWbsFilter`
 * auto-sets the shared `selectedWbsFilter` atom when it mounts with no value, which otherwise leaks
 * onto the platform KPI table after the panel closes. The drawer restores from this snapshot on close. */
export const kpiPanelWbsFilterSnapshot = atom<{ value: WbsFilterOption | undefined } | undefined>(
  undefined
);

export const qaqcPanelOpen = atom(false);

export const qaqcPanelOpenIntent = atom<QaqcPanelOpenIntent | undefined>(undefined);

export const selectedWorklogsTemplate = atom<LogsTemplate | undefined>();

export const selectedIndirectPobLogsTemplateForKpi = atom<LogsTemplate | undefined>();

export const selectedCostLogsTemplateForKpi = atom<LogsTemplate | undefined>();

export const selectedQaqcLogsTemplateForKpi = atom<QaqcKpiTemplate | undefined>();

// Selected workpack ID for KPI tracker
export const selectedWorkpackIdForKpiTracker = atom<string | undefined>();

export const selectedDate = atom<Date>(new Date());

export const selectedDeploymentIds = atomWithReset<string[]>([]);

export const selectedWorkpackTypes = atomWithReset<WorkpackTypeFilterOption[]>([]);

export const visibleColumnKeys = atomWithReset<string[] | undefined>(undefined);

export const kpiExportColumnKeys = atomWithReset<string[] | undefined>(undefined);

export const selectedDateAggregation = atomWithReset<AggregationGroupBy>(AggregationGroupBy.Daily);

export const selectedWbsFilter = atomWithReset<WbsFilterOption | undefined>(undefined);

export const selectedStartDate = atomWithReset<Date | undefined>(undefined);
export const selectedEndDate = atomWithReset<Date | undefined>(undefined);
export const shouldShowPobInput = atom<boolean>(false);

export const selectedShift = atom<ShiftType>(ShiftType.Day);

export const workpackComments = atom<
  Array<NonNullable<AllCommentsGenericQuery['allComments']>[number]>
>([]);

export const selectedLogTemplateType = atom<LogTemplateType>(LogTemplateType.WorklogTemplate);
